import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

const MENU = [
  { label: "All", to: "/shop" },
  { label: "Blankets", to: "/shop/blankets" },
  { label: "Towels", to: "/shop/towels" },
  { label: "Fragrance", to: "/shop/fragrance" },
  { label: "Collections", to: "/shop/collections" },
  { label: "Material Profile", to: "/material-science" },
  { label: "News", to: "/news" },
];

export const Navbar = () => {
  const { count, setOpen } = useCart();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const nav = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [hover, setHover] = useState(false);
  const [menu, setMenu] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 0);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const active = scrolled || hover;
  const darkTop = pathname === "/" || pathname === "/material-science";
  const light = active ? false : darkTop; // light = white text
  const textColor = light ? "text-white" : "text-black";

  return (
    <header
      data-testid="navbar"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={`fixed top-0 inset-x-0 z-50 ${active ? "bg-white border-b border-border" : "bg-transparent"}`}
      style={{ transition: "background-color 200ms ease, border-color 200ms ease" }}
    >
      <div className={`px-6 lg:px-12 h-16 flex items-center justify-between ${textColor}`} style={{ transition: "color 200ms ease" }}>
        {/* LEFT: hamburger + stream */}
        <div className="flex items-center gap-6 min-w-0">
          <button data-testid="hamburger" aria-label="Menu" onClick={() => setMenu(true)} className="flex flex-col gap-[5px] shrink-0 link-hover">
            <span className="block w-6 h-px bg-current" />
            <span className="block w-6 h-px bg-current" />
          </button>

          <Link to="/" data-testid="logo" className="font-display text-lg tracking-tight shrink-0 mr-2">rinova</Link>
        </div>

        {/* RIGHT: utility text stack */}
        <div className="flex flex-col items-end leading-[1.35] shrink-0">
          <button data-testid="util-search" onClick={() => toast("Search coming soon")} className="util-link link-hover">Search</button>
          <button data-testid="util-login" onClick={() => nav(user ? "/account" : "/login")} className="util-link link-hover">{user ? (user.name ? user.name.split(" ")[0] : "Account") : "Log in"}</button>
          <button data-testid="util-help" onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" })} className="util-link link-hover">Help</button>
          <button data-testid="util-bag" onClick={() => setOpen(true)} className="util-link link-hover">Bag ({count})</button>
        </div>
      </div>

      {/* FULL MENU OVERLAY */}
      <AnimatePresence>
        {menu && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-white text-black p-6 lg:p-12"
          >
            <div className="flex justify-between items-center mb-20">
              <span className="font-display text-2xl">rinova</span>
              <button data-testid="menu-close" onClick={() => setMenu(false)} className="util-link text-sm link-hover">Close</button>
            </div>
            <div className="flex flex-col gap-4 max-w-2xl">
              {MENU.map((m, i) => (
                <motion.button
                  key={m.to}
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.06 * i }}
                  data-testid={`menu-${m.to.split("/").pop()}`}
                  className="font-display text-4xl sm:text-6xl text-left leading-[0.95] link-hover"
                  onClick={() => { setMenu(false); nav(m.to); }}
                >
                  {m.label}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
